// Shared meta helpers for the English (/en) section.
//
// - `truncate` cuts strings to a max length at a word boundary and adds an
//   ellipsis, so meta descriptions never end mid-word.
// - `enLocaleMeta` returns the canonical og:locale / twitter tag set every
//   /en page should carry, so we never inherit the Turkish root defaults.
// - `enSocialMeta` bundles og:title / og:description / og:url plus their
//   twitter equivalents in one call.

export const EN_CONTACT_EMAIL = "ardaoztaskin@gmail.com";

/**
 * Cap a string at `max` characters, trimming at the last space so the
 * result never ends mid-word. Appends "…" when truncation happens.
 */
export function truncate(input: string, max = 155): string {
  const s = input.replace(/\s+/g, " ").trim();
  if (s.length <= max) return s;
  const slice = s.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(" ");
  const cut = lastSpace > 40 ? slice.slice(0, lastSpace) : slice;
  return cut.replace(/[,;:.—–-]+$/, "") + "…";
}

type MetaEntry =
  | { name: string; content: string }
  | { property: string; content: string };

/**
 * og:locale + og:locale:alternate + twitter:card for every /en page.
 * Placed early so it overrides the Turkish root defaults.
 */
export function enLocaleMeta(): MetaEntry[] {
  return [
    { property: "og:locale", content: "en_US" },
    { property: "og:locale:alternate", content: "tr_TR" },
    { name: "twitter:card", content: "summary_large_image" },
  ];
}

/**
 * Complete social meta block for an /en page. Descriptions are truncated
 * to 155 chars at a word boundary, and the same text is written to
 * og:description / twitter:description so the two never diverge.
 */
export function enSocialMeta(opts: {
  title: string;
  description: string;
  url: string;
  type?: string;
}): MetaEntry[] {
  const desc = truncate(opts.description);
  const type = opts.type ?? "website";
  return [
    { name: "description", content: desc },
    { property: "og:title", content: opts.title },
    { property: "og:description", content: desc },
    { property: "og:type", content: type },
    { property: "og:url", content: opts.url },
    { property: "og:locale", content: "en_US" },
    { property: "og:locale:alternate", content: "tr_TR" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: opts.title },
    { name: "twitter:description", content: desc },
  ];
}
